import { Link } from 'react-router-dom';

import { Glow, Icon, Logo } from '@/components/ui';
import { paths } from '@/routes/paths';

import { useSectionReveal } from './useSectionReveal';

/**
 * Footer navigation.
 *
 * Where a destination exists it is the real route. The rest point at a home
 * anchor named after the page they are waiting on -- `/#careers`, `/#privacy`
 * and so on -- so nothing 404s, and the slug says what still needs building.
 */
const COLUMNS = [
  {
    title: 'Students',
    links: [
      { to: paths.internships, label: 'Find internships' },
      { to: `${paths.home}#how-it-works`, label: 'How it works' },
      { to: `${paths.home}#profile-tips`, label: 'Profile tips' },
      { to: `${paths.home}#stories`, label: 'Stories' },
    ],
  },
  {
    title: 'Startups',
    links: [
      { to: paths.registerCompany, label: 'Hire talent' },
      { to: `${paths.home}#ai-matching`, label: 'AI matching' },
      { to: `${paths.forCompanies}#pricing`, label: 'Pricing' },
      { to: `${paths.home}#book-a-demo`, label: 'Book a demo' },
    ],
  },
  {
    title: 'Company',
    links: [
      { to: paths.about, label: 'About' },
      { to: `${paths.home}#careers`, label: 'Careers' },
      { to: paths.blog, label: 'Blog' },
      { to: `${paths.home}#contact`, label: 'Contact' },
    ],
  },
  {
    title: 'Legal',
    links: [
      { to: `${paths.home}#privacy`, label: 'Privacy' },
      { to: `${paths.home}#terms`, label: 'Terms' },
      { to: `${paths.home}#cookies`, label: 'Cookies' },
      { to: `${paths.home}#security`, label: 'Security' },
    ],
  },
];

/** Placeholder handles -- swap for the real accounts before launch. */
const SOCIALS = [
  { icon: 'x', label: 'Wonder on X', href: 'https://x.com' },
  { icon: 'linkedin', label: 'Wonder on LinkedIn', href: 'https://www.linkedin.com' },
  { icon: 'instagram', label: 'Wonder on Instagram', href: 'https://www.instagram.com' },
  { icon: 'facebook', label: 'Wonder on Facebook', href: 'https://www.facebook.com' },
];

export function SiteFooter() {
  // One three-second arrival, laid out on absolute positions rather than
  // chained, so a slow beat cannot shove the ones behind it out of time.
  //
  //   0.0  the orbs swell in behind everything
  //   0.1  the brand block rises out of focus
  //   0.6  the column headings, left to right
  //   0.9  every link cascades in under them, one after the next
  //   1.9  the socials pop
  //   2.2  the rule wipes across and the copyright follows it
  //
  // The orbs are scaled and never faded: `Glow` sets its own opacity inline
  // and a reveal would finish by animating that to 1, turning a soft orb into
  // a flat disc.
  const root = useSectionReveal(({ reveal }) => {
    reveal('.js-footer-glow', { scale: 0.55 }, { duration: 1.8, ease: 'power2.out' }, 0);
    reveal(
      '.js-footer-brand',
      { y: 34, opacity: 0, filter: 'blur(12px)' },
      { duration: 1, ease: 'power3.out' },
      0.1,
    );
    reveal(
      '.js-footer-title',
      { y: 24, opacity: 0, filter: 'blur(8px)' },
      { duration: 0.8, stagger: 0.09, ease: 'power3.out' },
      0.6,
    );
    // Staggered across all four columns as one list, not per column, so the
    // links read as a single wave crossing the footer instead of four
    // separate ones firing side by side.
    reveal(
      '.js-footer-link',
      { y: 16, opacity: 0 },
      { duration: 0.7, stagger: 0.045, ease: 'power2.out' },
      0.9,
    );
    reveal(
      '.js-footer-social',
      { y: 16, scale: 0.5, opacity: 0 },
      { duration: 0.6, stagger: 0.09, ease: 'back.out(2.4)' },
      1.9,
    );
    reveal(
      '.js-footer-rule',
      { scaleX: 0 },
      { duration: 0.8, ease: 'power3.inOut', transformOrigin: '0 50%' },
      2.2,
    );
    reveal('.js-footer-legal', { y: 12, opacity: 0 }, { duration: 0.6, ease: 'power2.out' }, 2.4);
  });

  return (
    <footer ref={root} className="border-night-line bg-night relative overflow-hidden border-t">
      <Glow className="js-footer-glow -bottom-40 -left-32" size={620} opacity={0.14} />
      <Glow className="js-footer-glow -top-32 right-1/4" size={520} tone="lilac" opacity={0.08} />

      <div className="wonder-container wonder-measure relative grid gap-12 py-16 lg:grid-cols-[1.8fr_1fr_1fr_1fr_1fr] lg:gap-8">
        <div className="js-footer-brand">
          <Link
            to={paths.home}
            aria-label="Wonder — home"
            className="text-on-night inline-flex items-center gap-3"
          >
            <Logo size={34} />
            {/*
              The wordmark is set here rather than inside Logo: the header uses
              the mark alone, and baking the text into the component would mean
              every caller had to opt out of it.
            */}
            <span className="text-[1.05rem] font-medium tracking-[0.22em]">WONDER</span>
          </Link>

          <p className="text-on-night-muted mt-5 max-w-xs text-[0.9375rem] leading-relaxed">
            Wonder today, work tomorrow. The AI-powered internship platform for ambitious students
            and startups.
          </p>

          <ul className="mt-6 flex gap-3">
            {SOCIALS.map((social) => (
              <li key={social.icon} className="js-footer-social">
                <a
                  href={social.href}
                  target="_blank"
                  rel="noreferrer noopener"
                  aria-label={social.label}
                  className="bg-iris-500/35 text-on-night hover:bg-iris-500/60 grid size-9 place-items-center rounded-full transition"
                >
                  <Icon name={social.icon} size={16} />
                </a>
              </li>
            ))}
          </ul>
        </div>

        {COLUMNS.map((column) => (
          <div key={column.title}>
            <p className="js-footer-title text-on-night text-[0.9375rem] font-semibold">
              {column.title}
            </p>
            <ul className="mt-4 space-y-3">
              {column.links.map((link) => (
                <li key={link.label} className="js-footer-link">
                  <Link
                    to={link.to}
                    className="text-on-night-muted hover:text-on-night text-[0.9375rem] transition"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className="wonder-container wonder-measure relative">
        <div className="js-footer-rule border-night-line border-t">
          <p className="js-footer-legal text-on-night-subtle py-6 text-center text-sm">
            © {new Date().getFullYear()} Wonder. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}
